import { createTheme, ThemeOptions } from '@mui/material/styles'
import { Primary, roboto, Secondary } from '@sphinx/components-styles'

export const theme = createTheme({
  palette: {
    primary: {
      main: Primary.orange[100],
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '100%',
          height: '32px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: '1px',
          fontSize: '12px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderWidth: '1px',
          borderColor: Primary.navy[40],
          borderRadius: '2px !important',
          boxSizing: 'border-box',
          background: Primary.neutral.white,
          '&:hover': {
            borderColor: Primary.navy[100],
          },
          '&.Mui-focused': {
            borderColor: Primary.orange[100],
            outline: 'none',
          },
          '&.Mui-disabled': {
            backgroundColor: Primary.navy[5],
            borderColor: Primary.navy[5],
            '& svg path': {
              fill: Primary.navy[20],
            },
          },
          '&.Mui-error': {
            backgroundColor: Secondary.red[10],
            borderColor: Secondary.red[100],
            '& input': {
              color: Secondary.red[100],
            },
            '& svg path': {
              fill: Secondary.red[100],
            },
            '& .MuiIconButton-root': {
              '&.Mui-focusVisible, &:hover': {
                backgroundColor: 'transparent',
                '& svg path': {
                  fill: Secondary.red[60],
                },
              },
            },
          },
        },
        input: {
          fontSize: '12px',
          height: '24px',
          color: Primary.navy[100],
          padding: '0px',
          '&::placeholder': {
            color: Primary.navy[60], // 20
          },
          '&:disabled': {
            color: Primary.navy[20],
          },
        },
        adornedEnd: {
          '& svg': {
            minWidth: '20px',
            minHeight: '20px',
            '& path': {
              fill: Primary.navy[100],
            },
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&::before': {
            border: 'none',
          },
          '&&:hover::before': {
            border: 'none',
          },
          '&::after': {
            border: 'none',
          },
          '&.Mui-disabled::before': {
            border: 'none',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          paddingTop: '5px',
          paddingBottom: '5px',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fill: Primary.neutral.black,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '& .MuiIconButton-root': {
            '&.Mui-focusVisible, &:hover': {
              backgroundColor: 'transparent',
              '& svg path': {
                fill: Primary.orange[100],
              },
            },
          },
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          fontFamily: roboto,
          color: Primary.neutral.black,
          fontWeight: 430,
          fontSize: '14px',
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: {
          fontFamily: roboto,
          color: Primary.neutral.black,
          fontWeight: 500,
          fontSize: '16px',
        },
        switchViewIcon: {
          fill: Primary.neutral.black,
          fontSize: '24px',
        },
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        root: {
          fontFamily: roboto,
          fontWeight: 400,
          color: Primary.neutral.black,
        },
        monthButton: {
          borderRadius: '0px',
          fontSize: '16px',
          '&.Mui-selected': {
            backgroundColor: Primary.orange[100],
            color: Primary.neutral.white,
            '&:focus': {
              backgroundColor: Primary.orange[100],
              color: Primary.neutral.white,
            },
            '&:hover': {
              backgroundColor: Primary.orange[100],
              border: `1px solid ${Primary.neutral.grey3}`,
            },
          },
          '&:hover': {
            backgroundColor: Primary.neutral.white,
            border: `1px solid ${Primary.neutral.grey3}`,
          },
          '&.Mui-disabled': {
            color: Primary.neutral.grey4,
          },
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          scrollbarWidth: 'none',
        },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        root: {
          fontFamily: roboto,
          fontWeight: 400,
          color: Primary.neutral.black,
        },
        yearButton: {
          borderRadius: '0px',
          fontSize: '16px',
          '&.Mui-selected': {
            backgroundColor: Primary.orange[100],
            color: Primary.neutral.white,
            '&:focus': {
              backgroundColor: Primary.orange[100],
              color: Primary.neutral.white,
            },
            '&:hover': {
              backgroundColor: Primary.orange[100],
              border: `1px solid ${Primary.neutral.grey3}`,
            },
          },
          '&:hover': {
            backgroundColor: Primary.neutral.white,
            border: `1px solid ${Primary.neutral.grey3}`,
          },
          '&.Mui-disabled': {
            color: Primary.neutral.grey4,
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          fontFamily: roboto,
          fontWeight: 420,
          color: Primary.neutral.black,
          borderRadius: '0px',
          fontSize: '14px',

          '&.Mui-selected': {
            '&:focus': {
              backgroundColor: Primary.orange[100],
            },
            '&:hover': {
              backgroundColor: Primary.orange[100],
              border: `1px solid ${Primary.neutral.grey3}`,

              '&::after': {
                top: '26px',
              },
            },
          },
          '&.Mui-disabled': {
            ':not(.Mui-selected)': {
              color: Primary.neutral.grey4,
            },
          },
          '&:hover': {
            backgroundColor: Primary.neutral.white,
            border: `1px solid ${Primary.neutral.grey3}`,
          },
        },
        today: {
          '&::after': {
            content: "''",
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            backgroundColor: Primary.neutral.white,
            position: 'absolute',
            top: '27px',
          },
          ':not(.Mui-selected)': {
            '&:focus': {
              backgroundColor: Primary.neutral.white,
            },
            border: 'none',
            '&:hover': {
              backgroundColor: Primary.neutral.white,
              border: `1px solid ${Primary.neutral.grey3}`,

              '&::after': {
                top: '26px',
              },
            },
            '&::after': {
              backgroundColor: Primary.orange[100],
            },
          },
        },
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: Primary.orange[100],
          '& .MuiTypography-root': {
            color: Primary.neutral.white,
          },
        },
        content: {
          fontFamily: roboto,
          color: Primary.neutral.white,
          '& .MuiTimePickerToolbar-hourMinuteLabel': {
            '& .MuiButtonBase-root': {
              width: '50px',
              '& .MuiPickersToolbarText-root': {
                opacity: 0.7,
                '&.Mui-selected': {
                  opacity: 1,
                  color: Primary.neutral.white,
                },
              },
            },
          },
          '& .MuiTimePickerToolbar-ampmSelection': {
            '& .MuiButtonBase-root': {
              width: '50px',
              '& .MuiPickersToolbarText-root': {
                opacity: 0.7,
                '&.Mui-selected': {
                  opacity: 1,
                  color: Primary.neutral.white,
                },
              },
            },
          },
          '& .MuiDateTimePickerToolbar-dateContainer': {
            '& .MuiButtonBase-root': {
              '& .MuiPickersToolbarText-root': {
                opacity: 0.7,
                '&.Mui-selected': {
                  opacity: 1,
                  color: Primary.neutral.white,
                },
              },
            },
          },
          '& .MuiDateTimePickerToolbar-timeContainer': {
            width: '150px',
            '& .MuiDateTimePickerToolbar-timeDigitsContainer': {
              width: '75%',
              '& .MuiButtonBase-root': {
                '& .MuiPickersToolbarText-root': {
                  opacity: 0.7,
                  '&.Mui-selected': {
                    opacity: 1,
                    color: Primary.neutral.white,
                  },
                },
              },
            },
            '& .MuiDateTimePickerToolbar-ampmSelection': {
              '& .MuiButtonBase-root': {
                width: '40px',
                '& .MuiPickersToolbarText-root': {
                  opacity: 0.7,
                  '&.Mui-selected': {
                    opacity: 1,
                    color: Primary.neutral.white,
                  },
                },
              },
            },
          },
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        root: {
          scrollbarWidth: 'none',
        },
        item: {
          fontFamily: roboto,
          fontWeight: 420,
          fontSize: '14px',
          color: Primary.neutral.black,
          border: `1px solid ${Primary.neutral.white}`,
          '&.Mui-selected': {
            border: `1px solid ${Primary.orange[100]}`,
            '&:hover': {
              backgroundColor: Primary.orange[100],
              border: `1px solid ${Primary.neutral.grey3}`,
            },
          },
          '&:hover': {
            backgroundColor: Primary.neutral.white,
            border: `1px solid ${Primary.neutral.grey3}`,
          },
          '&.Mui-disabled': {
            color: Primary.neutral.grey4,
            opacity: 1,
          },
        },
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          '& .MuiPickersLayout-actionBar': {
            '& .MuiButtonBase-root': {
              fontSize: '14px',
              borderRadius: '2px',
              '&:nth-of-type(1)': {
                backgroundColor: Primary.navy[5],
                border: `1px solid ${Primary.navy[20]}`,
                color: Primary.navy[100],
              },
              '&:nth-of-type(2)': {
                backgroundColor: Primary.navy[100],
                color: Primary.neutral.white,
              },
              '&:only-child': {
                backgroundColor: Primary.navy[100],
                color: Primary.neutral.white,
              },
            },
          },
        },
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          fontSize: '24px',
        },
      },
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
      },
    },
    MuiDateTimePickerTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-fixed': {
            '& .MuiTabs-flexContainer': {
              '& .MuiTab-root': {
                '& .MuiSvgIcon-root': {
                  fontSize: '24px',
                },
              },
            },
          },
        },
      },
    },
  },
  typography: {
    subtitle1: {
      fontSize: '16px',
    },
    caption: {
      fontSize: '12px',
    },
    overline: {
      fontSize: '12px',
    },
    h3: {
      fontSize: '48px',
    },
    h4: {
      fontSize: '34px',
    },
  },
} as ThemeOptions)
